import history from '../history';
import store from '../redux/store';
import { logoutUser } from '../redux/actions';
import swal from 'sweetalert';
import { setError } from '../redux/actions';

export async function handleResponse(response) {
	if (response.ok) return response.json();
	if (response.status === 400) {
		const error = await response.text();
		throw new Error(error);
	}
	throw new Error('Network response was not ok.');
}

// In a real app, would likely call an error logging service.
export function handleError(error) {
	// eslint-disable-next-line no-console
	console.error('API call failed. ' + error);
	throw error;
}

/** Normalize API error payload to a single string; never return blank. */
export function getErrorMessage(data) {
	if (!data) return '';
	const err = data.errors;
	if (Array.isArray(err) && err.length) return err.join(' ');
	if (err && typeof err === 'object') {
		const parts = Object.values(err).flat().filter(Boolean).map(String);
		if (parts.length) return parts.join(' ');
	}
	if (data.message && String(data.message).trim()) return data.message.trim();
	return '';
}

const publicAuthPaths = [
	'/login',
	'/signup',
	'/reset-password',
	'/forgot-password'
];

export function useInterceptor(axios) {
	// Enable cookies for all requests (httpOnly auth cookie)
	axios.defaults.withCredentials = true;

	axios.interceptors.request.use(
		(config) => {
			// Auth is cookie-only (withCredentials sends the httpOnly cookie)
			config.headers['Content-Type'] = 'application/json';
			return config;
		},
		(error) => {
			Promise.reject(error);
		}
	);

	axios.interceptors.response.use(
		(response) => {
			return response;
		},
		function (error) {
			const originalRequest = error.config;
			// No response = backend unreachable (connection refused, timeout, CORS, etc.)
			if (error.message === 'Network Error' || !error.response) {
				swal({
					title: 'Network Error',
					text: 'Cannot reach the server. Is the backend running at ' + (process.env.REACT_APP_BASE_URL || 'http://localhost:3000') + '?',
					icon: 'error'
				});
				return Promise.reject(error);
			}
			if (error.response.status === 304) {
				return Promise.resolve('Success')
			} 
			
			else if (error.response.status === 343) {
				history.push('/confirm');
			} 

			else if (error.response.status === 401) {
				// Already on a public auth page (e.g. restoreSession 401 on load) — don't redirect so ?return= / ?token= etc. are preserved
				const path = (typeof window !== 'undefined' && window.location)
					? (window.location.pathname || '')
					: '';
				if (publicAuthPaths.some(p => path.startsWith(p))) {
					return Promise.reject(error);
				}
				// Clear Redux so UI shows logged out (session invalid, email/role changed, etc.)
				store.dispatch(logoutUser());
				// Don't show modal or redirect for profile or logout — just reject. A 401 here on
				// a public page is completely normal (anonymous visitor, e.g. Googlebot or anyone
				// landing on /packages directly) and must not navigate them away from it. On a
				// private page, PrivateRoute already redirects to /login (preserving the origin
				// path) once currentUser clears — this used to also force everyone to '/' first,
				// which is both redundant there and actively wrong on public pages.
				if (error.config?.url?.includes('/user/profile') || error.config?.url?.includes('/logout')) {
					return Promise.reject(error);
				}
				// Redirect to login with return= current path so after sign-in they come back (e.g. group-trips, pay flow)
				const fullPath = (typeof window !== 'undefined' && window.location)
					? (window.location.pathname + (window.location.search || ''))
					: '';
				const returnTo = (fullPath && fullPath !== '/' && !fullPath.startsWith('/login') && !fullPath.startsWith('/signup'))
					? encodeURIComponent(fullPath)
					: '';
				history.push(returnTo ? '/login?return=' + returnTo : '/');
				swal({
					title: "Not Authorized",
					text: "Your session may have expired or changed. Please sign in again.",
					type: "warning",
					confirmButtonText: "OK"
				});
			}
			
			else if (error.response.status === 422) {
				// Signup OTP and Login: show error inline on the form, not in a modal
				if (error.config?.url?.includes('sign_up/request_otp') || error.config?.url?.includes('/login')) {
					return Promise.reject(error);
				}
				const errText = getErrorMessage(error.response.data);
				swal({
					title: 'Could not save',
					text: errText || 'Please check the form and try again.',
					icon: 'error',
					button: 'Try Again!'
				});
			} 
			
			else if (error.response.status === 400) {
				if (originalRequest?.skipGlobalError) {
					return Promise.reject(error);
				}
				const data = error.response.data;
				const ex = data?.exception || '';
				const title =
					ex === 'NilResponse' || ex.endsWith('::NilResponse')
						? 'No flights found'
						: (ex || 'Bad request');
				swal({
					title,
					text: data?.message || 'The request was invalid. Please try again.',
					icon: 'error',
					button: 'Try Again!'
				});
			} 
			
			else if (error.response.status === 404) {
				if (originalRequest?.skipGlobalError) {
					return Promise.reject(error);
				}
				const msg = getErrorMessage(error.response?.data) || 'The requested item was not found.';
				swal({
					title: 'Error',
					text: msg,
					icon: 'error',
					button: 'OK'
				});
			}

			else if (error.response.status === 500) {
				if (originalRequest?.skipGlobalError) {
					return Promise.reject(error);
				}
				const msg = getErrorMessage(error.response?.data) || error.message || 'Something went wrong on the server.';
				swal({
					title: 'Error',
					text: msg,
					icon: 'error',
					button: 'Try Again!'
				});
			}
			
			else {
				// Don't show error modal for logout — we already cleared Redux and redirected
				if (error.config?.url?.includes('/logout')) {
					return Promise.reject(error);
				}
				const msg = getErrorMessage(error.response?.data) || ('Request failed (' + (error.response?.status || '') + ').');
				swal({
					title: 'Error',
					text: msg,
					icon: 'error',
					button: 'OK'
				});
			}

			return Promise.reject(error);

		}
	);
}